import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
  RadioGroup,
  Radio,
  FormLabel,
  Box
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const SYSTEM_SOURCES = [
  { id: "anr", label: "All ANR Related Data" },
  { id: "users", label: "All Users from the System" },
  // …add more as needed
];

export default function FieldConfigDialog({
  dialogOpen,
  setDialogOpen,
  draftLabel,
  setDraftLabel,
  draftType,
  setDraftType,
  draftRequired,
  setDraftRequired,
  addField,
  pending,
}) {
  const [options, setOptions] = useState([]);
  const [optionSource, setOptionSource] = useState(
    "manual"
  );
  const [selectedSource, setSelectedSource] = useState("");

  // initialize on open
  useEffect(() => {
    if (dialogOpen) {
      setDraftLabel(pending?.label || "");
      setDraftType(pending?.type || "text");
      setDraftRequired(!!pending?.required);
      setOptions(pending?.options || []);
      // if pending has a dataSource, switch to system
      if (pending?.dataSource) {
        setOptionSource("system");
        setSelectedSource(pending.dataSource);
      } else {
        setOptionSource("manual");
        setSelectedSource("");
      }
    }
  }, [dialogOpen, pending]);

  const handleAddOption = () =>
    setOptions((opts) => [...opts, { label: "", value: "", valueType: "text" }]);

  const handleOptionChange = (i, key, val) => {
    const newOpts = [...options];
    newOpts[i][key] = val;
    setOptions(newOpts);
  };

  const handleSave = () => {
    const field = {
      ...pending,
      label: draftLabel,
      type: draftType,
      required: draftRequired,
    };
    if (["select", "radio"].includes(draftType) && optionSource === "system") {
      field.dataSource = selectedSource;
    } else if (["select", "radio"].includes(draftType)) {
      field.options = options;
    }
    addField(field);
    setDialogOpen(false);
  };

  return (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
      <DialogTitle>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ textAlign: "center" }}
        >
          Name & Configuration
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 500 }}
      >
        <TextField
          autoFocus
          label="Field Label"
          fullWidth
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
        />

        {["text"].includes(draftType) && (
          <FormControl fullWidth>
            <InputLabel id="type-label">Input Type</InputLabel>
            <Select
              labelId="type-label"
              value={draftType ?? "text"}
              label="Input Type"
              onChange={(e) => setDraftType(e.target.value)}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="number">Number</MenuItem>
            </Select>
          </FormControl>
        )}

        {["select", "radio"].includes(draftType) && (
          <>
            {/* 1) Choose Manual vs System */}
            <FormControl component="fieldset">
              <FormLabel component="legend">Option Source</FormLabel>
              <RadioGroup
                row
                value={optionSource}
                onChange={(e) =>
                  setOptionSource(e.target.value)
                }
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Manual"
                />
                <FormControlLabel
                  value="system"
                  control={<Radio />}
                  label="System Data"
                />
              </RadioGroup>
            </FormControl>

            {/* 2a) If system, pick your dataset */}
            {optionSource === "system" && (
              <FormControl fullWidth>
                <InputLabel id="system-source-label">
                  Data Source
                </InputLabel>
                <Select
                  labelId="system-source-label"
                  value={selectedSource ?? ""} 
                  label="Data Source"
                  onChange={(e) => setSelectedSource(e.target.value)}
                >
                  {SYSTEM_SOURCES.map((src) => (
                    <MenuItem key={src.id} value={src.id}>
                      {src.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* 2b) If manual, show Add Option */}
            {optionSource === "manual" && (
              <>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Add Option">
                    <IconButton onClick={handleAddOption}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2">
                    Add as many options as you like
                  </Typography>
                </Box>
                {options.map((opt, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TextField
                      label="Option Label"
                      value={opt.label}
                      onChange={(e) =>
                        handleOptionChange(i, "label", e.target.value)
                      }
                      fullWidth
                    />
                  </Box>
                ))}
              </>
            )}
          </>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={draftRequired}
              onChange={(e) => setDraftRequired(e.target.checked)}
            />
          }
          label="Required"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Add Field
        </Button>
      </DialogActions>
    </Dialog>
  );
}
